import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Text, Badge } from '@components/base/display';
import { Table } from '@components/base/data';
import { VStack } from '@components/base/layout';
import { Alert, Spinner } from '@components/base/feedback';
import { tenantsService } from '../../api/tenantsService';

interface TenantRegistriesTabProps {
  readonly tenantId: string;
}

export function TenantRegistriesTab({ tenantId }: TenantRegistriesTabProps) {
  const navigate = useNavigate();

  const {
    data: registriesData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['admin', 'tenants', tenantId, 'registries'],
    queryFn: () => tenantsService.listRegistries(tenantId),
  });

  const registries = registriesData?.data ?? [];

  // ---- Render: Loading ----

  if (isLoading) {
    return (
      <VStack align="center" justify="center" gap={2} className="py-12">
        <Spinner size="lg" />
        <Text color="muted">Loading registries...</Text>
      </VStack>
    );
  }

  // ---- Render: Error ----

  if (error) {
    return (
      <Alert variant="error" title="Failed to load registries">
        {error instanceof Error
          ? error.message
          : 'An unexpected error occurred while loading tenant registries.'}
      </Alert>
    );
  }

  // ---- Render: Main ----

  return (
    <VStack gap={6}>
      {/* Header */}
      <Text weight="medium">Associated Registries</Text>

      {/* Registries Table */}
      {registries.length === 0 ? (
        <Alert variant="info" title="No registries associated">
          This tenant has no ERP registries associated yet.
        </Alert>
      ) : (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Name</Table.HeaderCell>
              <Table.HeaderCell>Base URL</Table.HeaderCell>
              <Table.HeaderCell>Auth Type</Table.HeaderCell>
              <Table.HeaderCell align="center">Status</Table.HeaderCell>
              <Table.HeaderCell>Endpoints</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {registries.map((registry) => (
              <Table.Row
                key={registry.id}
                onClick={() =>
                  navigate(`/admin/erp/registries/${registry.slug}`)
                }
              >
                <Table.Cell>
                  <Text weight="medium">{registry.name}</Text>
                </Table.Cell>
                <Table.Cell>
                  <Text size="sm" color="muted">
                    {registry.base_url}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <Badge variant="default" size="sm">
                    {registry.auth_type}
                  </Badge>
                </Table.Cell>
                <Table.Cell align="center">
                  <Badge
                    variant={registry.is_active ? 'success' : 'default'}
                    size="sm"
                  >
                    {registry.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <Text size="sm" color="muted">
                    {registry.endpoints_count}
                  </Text>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </VStack>
  );
}
